
import ConfigUnits from '../assets/images/ConfigPageImage.png';
import ConfigUnitsImg from '../assets/images/configUnit.png';
import { getAllProperties, getProperty } from '../lib/api';
import useFleetStore from '../utils/store';
import Dropdown from './Common/Dropdown';
import Input from '../components/Common/Input';
import Label from '../components/Common/Label';
import { useCallback, useEffect, useRef, useState } from 'react';
import { updateUnassociatedUnit } from '../lib/api';
import {
    assignUnitToZone,
    isAwaitingConnection,
    PENDING_ZONE_ASSIGNED_MESSAGE,
    PENDING_ZONE_HEADING,
    PENDING_ZONE_HELPER_TEXT,
    PENDING_ZONE_REMOVED_MESSAGE,
} from '../utils/unitZoneUtils';
import PropertyCard from './PropertyCard';
import BuildingCard from './BuildingCard';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmModal from './Common/ConfirmModal';
import PodModal from './PodModals/CreatePodModal';
import ToastMessage from './Common/ToastMessage';
import Icon from "../assets/icons/Icon";
import CloseIcon from '../assets/icons/close_icon.svg'
import { useIsWriteRestrictedUser } from "../hooks/useIsWriteRestrictedUser";
import { UNIT_LIST_PATHS, unitListReturnPath } from '../utils/unitListReturn';
import { readLastSelectedLocation, saveLastSelectedLocation } from '../utils/lastSelectedLocation';

const buildZoneAssignmentMessage = (awaitingConnection, zone) => {
    if (!zone) {
        return awaitingConnection
            ? PENDING_ZONE_REMOVED_MESSAGE
            : "Unit removed from zone successfully";
    }
    return awaitingConnection ? PENDING_ZONE_ASSIGNED_MESSAGE : "Unit added to zone successfully";
};

const ConfigureUnits = () => {
    const isActionsDisabled = useIsWriteRestrictedUser();
    const selectedUnit = useFleetStore((state) => state.selectedUnit);
    const setSelectedUnit = useFleetStore((state) => state.setSelectedUnit);
    const awaitingConnection = isAwaitingConnection(selectedUnit);
    const [allProps, setAllProps] = useState([]);
    const [selectedZone, setSelectedZone] = useState(null);
    const [isZoneManuallySelected, setIsZoneManuallySelected] = useState(false);
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [unitForm, setUnitForm] = useState(
        {
            name: '',
            propertyUnid: '',
            buildingUnid: null,
            floor: null,
            room: '',
            unit: ''
        }
    );
    const [toast, setToast] = useState({
        show: false,
        message: '',
        type: 'success',
    });
    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [isBuildingDisabled, setIsBuildingDisabled] = useState(true);
    const [showBuildingModal, setShowBuildingModal] = useState(false);
    const [selectedBuildingName, setSelectedBuildingName] = useState('');
    const [editUnitName, setEditUnitName] = useState(false);
    const [showFleetDropdown, setShowFleetDropdown] = useState(false);
    const [showFleetSection, setShowFleetSection] = useState('');
    const [locationDetails, setLocationDetails] = useState();
    const dropdownFleetRef = useRef(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [createZoneModal, setShowCreateZoneModal] = useState(false);
    const { allPodData, setAllPodData, setSelectedFleet } = useFleetStore();
    const [propertyNameValue, setPropertyNameValue] = useState('');
    const [propertyNameID, setPropertyNameID] = useState('');
    const [showUnitChangeModel, setShowUnitChangeModel] = useState(false);
    const [pendingProperty, setPendingProperty] = useState(null);
    const [originalUnitName, setOriginalUnitName] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const returnPath = unitListReturnPath(location.state, UNIT_LIST_PATHS.manageunits);

    const [errors, setErrors] = useState({});

    const fetchAllProperty = useCallback(async () => {
        const payload = {
            pageNumber: 1,
            pageSize: 100,
        };
        try {
            const res = await getAllProperties(payload);

            if (res?.status === 200) {
                setAllProps(res?.data?.data);
                const names = res?.data?.data?.properties?.map((item) => item.name) || [];
                setPropertyOptions(names);
            }
        } catch (error) {
            console.error('Failed to fetch pods:', error);
        }
    }, [setAllProps]);

    useEffect(() => {
        fetchAllProperty();
    }, [fetchAllProperty]);

    const fetchAllBuildingValues = async (newBuilding) => {
        try {
            const response = await getProperty({ unid: propertyNameID });
            const buildings = response?.data?.data?.property?.buildings?.map((b) => b.name) || [];
            setBuildingOptions(buildings);
            handleBuildingSelect(newBuilding?.name);
        } catch (error) {
            console.error("Error fetching property data:", error);
        }
    }

    useEffect(() => {
        if (selectedUnit?.metadata) {
            const metadata = selectedUnit.metadata;

            setUnitForm({
                name: metadata.name || '',
                propertyUnid: metadata.propertyUnid || '',
                buildingUnid: metadata.buildingUnid || null,
                floor: metadata.floor || null,
                room: metadata.room || '',
                unit: metadata.unit || ''
            });

            setPropertyOptions((prev) => [...new Set([metadata.propertyName, ...prev])]);

            const selectedProperty = allProps?.properties?.find(
                (prop) => prop.unid === metadata.propertyUnid
            );

            const buildings = selectedProperty?.buildings?.map((b) => b.name) || [];
            setBuildingOptions(buildings);

            if (metadata.buildingName || (buildings?.length)) {
                setSelectedBuildingName(metadata.buildingName);
                setIsBuildingDisabled(false);
            } else {
                setIsBuildingDisabled(true);
            }
        }
    }, [selectedUnit, selectedUnit?.metadata, allProps]);

    useEffect(() => {
        if (selectedUnit?.metadata) {
            const metadata = selectedUnit.metadata;

            const selectedProperty = allProps?.properties?.find(
                (prop) => prop.unid === metadata.propertyUnid
            );
            setPropertyNameValue(selectedProperty?.name);
        }
    }, [allProps, selectedUnit?.metadata])

    const unassociatedSerialNumber = useRef(null);
    const restoredForSerialNumber = useRef(null);

    const rememberLocation = (location) => {
        if (unassociatedSerialNumber.current !== selectedUnit?.serialNumber) return;
        saveLastSelectedLocation(location);
    };

    useEffect(() => {
        if (!selectedUnit || selectedUnit.serialNumber === unassociatedSerialNumber.current) return;
        if (!selectedUnit.metadata?.propertyUnid) {
            unassociatedSerialNumber.current = selectedUnit.serialNumber;
        }
    }, [selectedUnit]);

    useEffect(() => {
        const metadata = selectedUnit?.metadata;
        if (!selectedUnit || !allProps?.properties || metadata?.propertyUnid) return;
        if (restoredForSerialNumber.current === selectedUnit.serialNumber) return;
        restoredForSerialNumber.current = selectedUnit.serialNumber;

        const remembered = readLastSelectedLocation();
        const property = allProps.properties.find((prop) => prop.unid === remembered?.propertyUnid);
        if (!property) return;

        const building = property.buildings?.find((b) => b.unid === remembered.buildingUnid);
        const last4 = selectedUnit.serialNumber?.slice(-4) || '';

        setPropertyNameID(property.unid);
        setSelectedUnit({
            ...selectedUnit,
            metadata: {
                ...metadata,
                propertyUnid: property.unid,
                propertyName: property.name,
                buildingUnid: building?.unid || null,
                buildingName: building?.name || '',
                name: metadata?.name || `${property.name}-${last4}`,
            },
        });
    }, [selectedUnit, allProps, setSelectedUnit]);

    useEffect(() => {
        if (selectedUnit?.pendingZone) {
            setSelectedZone(selectedUnit.pendingZone);
            setIsZoneManuallySelected(true);
        }
    }, [selectedUnit?.pendingZone])

    const handleChangeUnit = (e) => {
        e.preventDefault();
        if (!pendingProperty) return;

        const propertyUnid = pendingProperty.unid;
        rememberLocation({ propertyUnid });
        const firstWord = pendingProperty.name;
        const last4 = selectedUnit?.serialNumber?.slice(-4) || '';
        const unitName = `${firstWord}-${last4}`;

        setPropertyNameValue(pendingProperty.name);
        setPropertyNameID(pendingProperty.unid);

        setUnitForm((prev) => ({
            ...prev,
            propertyUnid,
            name: unitName,
            buildingUnid: null
        }));

        if (unitName.trim() !== '') {
            setErrors((prev) => {
                const newErrors = { ...prev };
                newErrors.name = '';
                return newErrors;
            });
        }

        const buildings = pendingProperty.buildings?.map((b) => b.name) || [];
        setBuildingOptions(buildings);
        setIsBuildingDisabled(buildings.length === 0);

        const newSelectedUnit = {
            ...selectedUnit,
            metadata: {
                ...selectedUnit.metadata,
                propertyUnid,
                propertyName: pendingProperty.name,
                buildingUnid: null,
                buildingName: '',
                name: unitName
            }
        };
        setSelectedUnit(newSelectedUnit);

        // Cleanup
        setPendingProperty(null);
        setShowUnitChangeModel(false);
    };


    const handleCancelChangeUnit = (e) => {
        e.preventDefault();

        if (!pendingProperty) {
            setShowUnitChangeModel(false);
            return;
        }

        const propertyUnid = pendingProperty.unid;
        rememberLocation({ propertyUnid });

        setPropertyNameValue(pendingProperty.name);
        setPropertyNameID(propertyUnid);

        setUnitForm((prev) => ({
            ...prev,
            propertyUnid,
            buildingUnid: null,
            name: originalUnitName  // ✅ Restore manually typed name
        }));

        const buildings = pendingProperty.buildings?.map((b) => b.name) || [];
        setBuildingOptions(buildings);
        setIsBuildingDisabled(buildings.length === 0);

        const newSelectedUnit = {
            ...selectedUnit,
            metadata: {
                ...selectedUnit.metadata,
                propertyUnid,
                propertyName: pendingProperty.name,
                buildingUnid: null,
                buildingName: '',
                name: originalUnitName // ✅ Restore in metadata too
            }
        };
        setSelectedUnit(newSelectedUnit);

        // Cleanup
        setPendingProperty(null);
        setOriginalUnitName('');
        setShowUnitChangeModel(false);
    };

    const handlePropertySelect = (propertyName) => {
        const selectedProperty = allProps?.properties?.find(
            (prop) => prop.name === propertyName
        );
        if (!selectedProperty) return;

        // ✅ Store manually edited unit name (before any updates)
        if (unitForm?.name) {
            setOriginalUnitName(unitForm.name);

            // ✅ Show modal and hold property temporarily
            setPendingProperty(selectedProperty);
            setShowUnitChangeModel(true);
        } else {
            const propertyUnid = selectedProperty.unid;
            rememberLocation({ propertyUnid });
            const firstWord = selectedProperty.name;
            const last4 = selectedUnit?.serialNumber?.slice(-4) || '';
            const unitName = `${firstWord}-${last4}`;

            setPropertyNameValue(selectedProperty.name);
            setPropertyNameID(selectedProperty.unid);

            setUnitForm((prev) => ({
                ...prev,
                propertyUnid,
                name: unitName,
                buildingUnid: null
            }));

            if (unitName.trim() !== '') {
                setErrors((prev) => {
                    const newErrors = { ...prev };
                    newErrors.name = '';
                    return newErrors;
                });
            }

            const buildings = selectedProperty.buildings?.map((b) => b.name) || [];
            setBuildingOptions(buildings);
            setIsBuildingDisabled(buildings.length === 0);

            const newSelectedUnit = {
                ...selectedUnit,
                metadata: {
                    ...selectedUnit.metadata,
                    propertyUnid,
                    propertyName: selectedProperty.name,
                    buildingUnid: null,
                    buildingName: '',
                    name: unitName
                }
            };
            setSelectedUnit(newSelectedUnit);
        }
    };

    const handleBuildingSelect = (value) => {
        console.log(value);
        if (value === "Remove Building") {
            setSelectedBuildingName(null);
            rememberLocation({ propertyUnid: unitForm.propertyUnid });
            setUnitForm(prev => {
                const updated = { ...prev, buildingName: null, buildingUnid: null };
                return updated;
            });
            return;
        } else {

            console.log(unitForm);

            setSelectedBuildingName(value);

            const selectedProperty = allProps?.properties?.find(
                (prop) => prop.unid === unitForm.propertyUnid
            );

            if (selectedProperty) {
                const selectedBuilding = selectedProperty.buildings.find(
                    (building) => building.name === value
                );
                console.log("building added", value)

                if (selectedBuilding) {
                    rememberLocation({
                        propertyUnid: selectedProperty.unid,
                        buildingUnid: selectedBuilding.unid,
                    });
                    setUnitForm((prev) => ({
                        ...prev,
                        buildingUnid: selectedBuilding.unid,
                    }));
                    console.log("building added", selectedBuilding.unid)
                    setErrors((prev) => ({
                        ...prev,
                        buildingUnid: null,
                    }));
                } else {
                    console.log("Selected building not found:", value);
                }
            } else {
                console.log("No property selected yet");
            }
        }
    };

    const handleBuildingCreated = (newBuilding) => {
        if (!newBuilding) {
            fetchAllBuildingValues();
            return;
        }

        setBuildingOptions((prev) => {
            if (prev.includes(newBuilding.name)) return prev;
            return [...prev, newBuilding.name];
        });

        rememberLocation({
            propertyUnid: unitForm.propertyUnid,
            buildingUnid: newBuilding.unid,
        });

        const newSelectedUnit = {
            ...selectedUnit,
            metadata: {
                ...selectedUnit.metadata,
                buildingUnid: newBuilding.unid,
                buildingName: newBuilding.name,
            }
        }
        setSelectedUnit(newSelectedUnit);

        setAllProps((prev) => {
            if (!prev.properties) return prev;
            const updatedProperties = prev.properties.map((prop) => {
                if (prop.name === propertyNameValue) {
                    return {
                        ...prop,
                        buildings: [...(prop.buildings || []), newBuilding],
                    };
                }
                return prop;
            });
            return { ...prev, properties: updatedProperties };
        });

        setIsBuildingDisabled(false);

        setShowBuildingModal(false);
        showToast("Building created successfully", "success");
    };

    const onPropertyCreated = (newProperty) => {
        const { name, unid } = newProperty?.property || {};
        if (!name || !unid) return;
        showToast('Property added successfully', 'success');
        // ✅ Set pending property same as dropdown logic
        const tempNewProperty = {
            name,
            unid,
            buildings: [] // You can pass buildings here if available
        };

        // ✅ Store current name to preserve if cancelled
        setOriginalUnitName(unitForm.name);

        // ✅ Store property temporarily and show modal
        setPendingProperty(tempNewProperty);
        setShowUnitChangeModel(true);

        // ✅ Refresh property list
        fetchAllProperty();
    };

    const handleRemoveZone = () => {
        setSelectedZone(null);
        setIsZoneManuallySelected(false);
    };

    const handleChange = (e) => {
        e.preventDefault();
        const { name, value } = e.target;

        let errorMsg = '';

        if (name === 'floor') {
            const numericValue = value.replace(/\D/g, '');

            if (numericValue.length > 15) {
                errorMsg = 'Floor number cannot exceed 15 digits.';
                setErrors((prev) => ({
                    ...prev,
                    [name]: errorMsg,
                }));
                return;
            }
            errorMsg = '';
            setUnitForm((prev) => ({
                ...prev,
                [name]: numericValue ? Number(numericValue) : null,
            }));

        } else if (name === 'room') {
            if (value.length > 15) {
                errorMsg = 'Room number cannot exceed 15 characters.';
                setErrors((prev) => ({
                    ...prev,
                    [name]: errorMsg,
                }));
                return; // ⛔ Prevent state update
            }

            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }));

            setUnitForm((prev) => ({
                ...prev,
                [name]: value,
            }));
        } else if (name === 'unit') {
            if (value.length > 15) {
                errorMsg = 'Apartment name cannot exceed 15 characters.';
                setErrors((prev) => ({
                    ...prev,
                    [name]: errorMsg,
                }));
                return;
            }

            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }));

            setUnitForm((prev) => ({
                ...prev,
                [name]: value,
            }));
        } else {
            setUnitForm((prev) => ({
                ...prev,
                [name]: value,
            }));
        }

        setErrors((prev) => ({
            ...prev,
            [name]: errorMsg,
        }));
        setErrors((prev) => {
            const updatedErrors = { ...prev };

            if (name === 'name') {
                updatedErrors[name] = value.trim() === '' ? 'Enter a Unit Name.' : '';
            }

            if (name === 'floor') {
                updatedErrors[name] = errorMsg;
            }

            if (name === 'room') {
                updatedErrors[name] = errorMsg;
            }

            if (name === 'unit') {
                updatedErrors[name] = errorMsg;
            }


            return updatedErrors;
        });
    };



    const validate = () => {
        let valid = true;
        const newErrors = {};
        if (!unitForm.propertyUnid) {
            newErrors.Property = 'Select any Property';
            valid = false;
        }

        const floorValue = String(unitForm.floor ?? '');

        if (floorValue && floorValue.length > 15) {
            newErrors.floor = 'Floor number cannot exceed 15 digits.';
            valid = false;
        }

        const roomValue = String(unitForm.room ?? '');

        if (roomValue && roomValue.length > 15) {
            newErrors.room = 'Room number cannot exceed 15 digits.';
            valid = false;
        }

        const apartmentValue = String(unitForm.unit ?? '');

        if (apartmentValue && apartmentValue.length > 15) {
            newErrors.unit = 'Apartment number cannot exceed 15 digits.';
            valid = false;
        }

        if (!unitForm.name) {
            newErrors.name = 'Enter a Unit Name.';
            valid = false;
        }
        setErrors(newErrors);
        return valid
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isActionsDisabled) return;

        if (!validate()) return;

        const payload = {
            serialNumber: selectedUnit?.serialNumber,
        };

        const body = awaitingConnection
            ? { ...unitForm, zoneUnid: (isZoneManuallySelected && selectedZone?.unid) || null }
            : unitForm;

        try {
            const res = await updateUnassociatedUnit(payload, body);
            if (res.status === 200) {
                setLocationDetails(res.data);
            }
            setShowFleetSection(true);
        } catch (error) {
            if (error.message === "Failed to fetch") {
                showToast("Network error. Please try again.", 'failed')
            } else {
                console.error("Could not save location details", error);
                showToast("Could not save location details. Please try again.", 'failed')
            }

        }
    };

    const handlePropertyAdd = (e) => {
        e.preventDefault();
        if (isActionsDisabled) return;
        setShowPropertyModal(true);
    };

    const handleBuildingAdd = (e) => {
        e.preventDefault();
        if (isActionsDisabled) return;
        setShowBuildingModal(true);
    }

    const handleFleetDropdown = () => {
        setShowFleetDropdown((prev) => !prev);
    }

    const handleSelectFleet = (fleet) => {
        setSelectedZone(fleet);
        setIsZoneManuallySelected(true);
    };

    const handleEditUnitName = () => {
        setEditUnitName((prev) => !prev);
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownFleetRef.current && !dropdownFleetRef.current.contains(event.target)) {
                setShowFleetDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const handleAddUnitToFleet = async (e) => {
        e.preventDefault();
        if (isActionsDisabled) return;

        const unitToAssign = {
            ...selectedUnit,
            metadata: {
                ...(locationDetails?.data?.unit?.metadata || selectedUnit?.metadata),
                name: unitForm.name,
            },
        };

        const selectedFleetData = (isZoneManuallySelected && selectedZone) || null;

        const isClearingPendingZone = awaitingConnection && !!selectedUnit?.pendingZone;
        if (!selectedFleetData && !isClearingPendingZone) {
            navigate(returnPath);
            return;
        }

        if (selectedFleetData) {
            setSelectedFleet(selectedFleetData);
        }

        try {
            const res = await assignUnitToZone(unitToAssign, selectedFleetData);
            if (res.status === 200) {
                navigate(returnPath, {
                    state: {
                        toast: {
                            message: buildZoneAssignmentMessage(awaitingConnection, selectedFleetData),
                            type: "success",
                        },
                    },
                });
            }
        } catch (error) {
            if (error.message === "Failed to fetch") {
                showToast("Network error. Please try again.", "failed");
            } else {
                console.error("Could not save the unit's zone", error);
                showToast("Could not save the unit. Please try again.", "failed");
            }
        }
    };

    const handleGoBack = (e) => {
        e.preventDefault();
        setShowConfirmModal(true);
    };

    const handleCancelDelete = (e) => {
        e.preventDefault();
        setShowConfirmModal(false);
    }

    const handleConfirmBack = (e) => {
        e.preventDefault();
        navigate(-1);
    }

    const handleCreateZoneModal = (e) => {
        e.preventDefault();
        if (isActionsDisabled) return;
        setShowCreateZoneModal(true);
    }

    return (
        <>
            {selectedUnit && (
                <div className="flex-1 flex flex-col">
                    <div className="flex w-full gap-6">
                        <div className="flex gap-2 items-start border-b border-[#00000026] pb-5 grow">
                            <div className="closeIconHolder cursor-pointer hidden" onClick={handleGoBack}>
                                <Icon src={CloseIcon} alt="close" className="w-6 h-6" />
                            </div>
                            <h2 className="mainTitle text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px] ">Configure Unit</h2>
                        </div>
                    </div>
                    <div className="flex gap-6 mt-6 grow items-start relative">
                        <div className="formHolder basis-full lg:basis-8/12 shrink grow-0 w-full">
                            <div className="w-full h-full">
                                <div className="flex flex-col gap-y-6 lg:gap-y-8 h-full">
                                    <form onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}>
                                        <div className="flex items-start flex-col sm:flex-row gap-8 gap-y-4">
                                            <div className="unitID_section gap-1 flex flex-col w-full sm:w-56 md:w-[272px]">
                                                <div className="flex flex-col gap-1">
                                                    <Label
                                                        htmlFor="unitID"
                                                        className="text-dark-grey dark:text-white text:dark-white font-normal text-sm leading-4 xl:text-base lg:leading-5 tracking-[-0.24px]"
                                                    >
                                                        Serial Number
                                                    </Label>
                                                    <Input
                                                        id="unitID"
                                                        name="unit"
                                                        type="text"
                                                        placeholder="Enter Serial Number"
                                                        className="text-base leading-5 lg:text-lg xl:leading-6 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg placeholder:text-dark-grey/40 dark:placeholder:text-white/40 dark:text-white font-medium tracking-[-0.408px] outline-none opacity-40 pointer-events-none"
                                                        value={selectedUnit?.serialNumber || ''}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className='basis-10 shrink-0 hidden sm:flex'>
                                                <button className="text-theme text-base font-semibold leading-6 hidden">Add</button>
                                            </div>
                                            <div className="flex items-center gap-4 w-full md:w-auto">
                                                <div className="unitID_section gap-1 flex flex-col w-full">
                                                    <div className="flex flex-col gap-1 relative">
                                                        <Label htmlFor="unitName" className="text-dark-grey dark:text-white font-normal text-sm leading-4 xl:text-base lg:leading-5 tracking-[-0.24px]">Unit Name</Label>
                                                        <div className="flex items-center gap-4">
                                                            <div className='w-full sm:w-auto'>
                                                                <input
                                                                    id="unitName"
                                                                    type="text"
                                                                    name="name"
                                                                    value={unitForm.name}
                                                                    placeholder="Enter Unit Name"
                                                                    className={`text-base leading-5 lg:text-lg xl:leading-6 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey h-11 bg-white dark:darkThemeGradiantBg placeholder:text-dark-grey/40 dark:placeholder:text-white/40 dark:text-white font-medium tracking-[-0.408px] outline-none ${!editUnitName ? "opacity-40 pointer-events-none" : ""} w-full sm:w-[186px] md:w-[236px]`}
                                                                    onChange={handleChange}
                                                                />

                                                            </div>
                                                            {!showFleetSection && (
                                                                <div className="editIconHolder cursor-pointer flex justify-end  sm:justify-start basis-10 shrink-0 " onClick={handleEditUnitName}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className='*:dark:stroke-theme'>
                                                                        <path d="M22 10.5V12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2H13.5" stroke="#1E2A2C" strokeWidth="1.5" strokeLinecap="round" />
                                                                        <path d="M16.652 3.45506L17.3009 2.80624C18.3759 1.73125 20.1188 1.73125 21.1938 2.80624C22.2687 3.88124 22.2687 5.62415 21.1938 6.69914L20.5449 7.34795M16.652 3.45506C16.652 3.45506 16.7331 4.83379 17.9497 6.05032C19.1662 7.26685 20.5449 7.34795 20.5449 7.34795M16.652 3.45506L10.6872 9.41993C10.2832 9.82394 10.0812 10.0259 9.90743 10.2487C9.70249 10.5114 9.52679 10.7957 9.38344 11.0965C9.26191 11.3515 9.17157 11.6225 8.99089 12.1646L8.41242 13.9M20.5449 7.34795L14.5801 13.3128C14.1761 13.7168 13.9741 13.9188 13.7513 14.0926C13.4886 14.2975 13.2043 14.4732 12.9035 14.6166C12.6485 14.7381 12.3775 14.8284 11.8354 15.0091L10.1 15.5876M10.1 15.5876L8.97709 15.9619C8.71035 16.0508 8.41626 15.9814 8.21744 15.7826C8.01862 15.5837 7.9492 15.2897 8.03811 15.0229L8.41242 13.9M10.1 15.5876L8.41242 13.9" stroke="#1E2A2C" strokeWidth="1.5" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {errors.name && (
                                                            <p className='text-critical text-xs leading-4 absolute -bottom-5'>
                                                                {errors.name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>

                                    {!showFleetSection ? (
                                        <div className="locationsInfoHolder h-full flex flex-col gap-6">
                                            <h3 className="text-black dark:text-white text-lg leading-5 lg:text-xl lg:leading-6 font-semibold tracking-[0.03px]">Location Details</h3>
                                            <form className="h-full flex flex-col gap-12">
                                                <div className="flex flex-col gap-6 gap-y-4 grow">
                                                    <Dropdown
                                                        // options={allProps.properties}
                                                        options={propertyOptions}
                                                        onSelect={handlePropertySelect}
                                                        sectionClass="propertysection"
                                                        id="property"
                                                        name="Property"
                                                        value={propertyNameValue ? propertyNameValue : "Select a Property"}
                                                        defaultValue={selectedUnit?.metadata?.propertyName ? selectedUnit?.metadata?.propertyName : "Select a Property"}
                                                        onClick={handlePropertyAdd}
                                                        disabled={isActionsDisabled}
                                                    />
                                                    {errors.Property && (
                                                        <p className='text-critical text-xs leading-4'>
                                                            {errors.Property}
                                                        </p>
                                                    )}

                                                    <div className="flex items-start flex-col sm:flex-row gap-12 gap-y-4">
                                                        <div className='w-full sm:w-auto'>
                                                            <Dropdown
                                                                sectionClass="buildingSection w-full sm:w-auto"
                                                                options={buildingOptions}
                                                                onSelect={handleBuildingSelect}
                                                                id="building"
                                                                name="Building"
                                                                disabled={isBuildingDisabled || isActionsDisabled}
                                                                addDisabled={isActionsDisabled || !unitForm.propertyUnid}
                                                                onClick={handleBuildingAdd}
                                                                value={
                                                                    buildingOptions.includes(selectedBuildingName)
                                                                        ? selectedBuildingName
                                                                        : "Select a Building"
                                                                }
                                                            />
                                                            {errors.buildingUnid && (
                                                                <p className='text-critical text-xs leading-4'>
                                                                    {errors.buildingUnid}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div className="apartmentSection w-full sm:w-auto">
                                                            <div className="flex flex-col gap-1">
                                                                <Label htmlFor="unit" className="text-dark-grey dark:text-white font-normal text-sm leading-4 xl:text-base lg:leading-5 tracking-[-0.24px]">Apartment</Label>
                                                                <div className="flex items-center gap-4">
                                                                    <div className="flex items-center gap-2 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey dark:text-white h-11 bg-white dark:darkThemeGradiantBg  md:w-[272px] sm:w-56 w-full">
                                                                        <input
                                                                            id="unit"
                                                                            type="text"
                                                                            name="unit"
                                                                            value={unitForm.unit}
                                                                            placeholder="Enter Apartment"
                                                                            onChange={handleChange}
                                                                            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                                                                            className="w-full text-base leading-5 lg:text-lg xl:leading-6  placeholder:text-dark-grey/40 dark:placeholder:text-white/40 dark:bg-transparent font-medium tracking-[-0.408px] outline-none"
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {errors.unit && (
                                                                    <p className="text-critical text-xs leading-4 mt-1">
                                                                        {errors.unit}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-start flex-col sm:flex-row gap-12 gap-y-4">
                                                        <div className="floorSection w-full sm:w-auto">
                                                            <div className="flex flex-col gap-1">
                                                                <Label htmlFor="floor" className="text-dark-grey dark:text-white font-normal text-sm leading-4 xl:text-base lg:leading-5 tracking-[-0.24px]">Floor</Label>
                                                                <div className="flex items-center gap-4">
                                                                    <div className="flex items-center gap-2 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey dark:text-white h-11 bg-white dark:darkThemeGradiantBg md:w-[272px] sm:w-56  w-full shrink-0">
                                                                        <input
                                                                            id="floor"
                                                                            type="number"
                                                                            name="floor"
                                                                            value={unitForm.floor}
                                                                            placeholder="Enter Floor"
                                                                            className="w-full text-base leading-5 lg:text-lg xl:leading-6  placeholder:text-dark-grey/40 dark:placeholder:text-white/40 dark:text-white dark:bg-transparent font-medium tracking-[-0.408px] outline-none"
                                                                            onChange={handleChange}
                                                                            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                                                                        />
                                                                        <div className="dropdownIconHolder hidden">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                                <path d="M19.92 8.9502L13.4 15.4702C12.63 16.2402 11.37 16.2402 10.6 15.4702L4.08002 8.9502" stroke="#FF8C65" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                    <div className="addIconHolder justify-center items-center basis-10 w-[30px] shrink-0 hidden sm:flex">
                                                                        <button className="text-theme text-base font-semibold leading-6 hidden">Add</button>
                                                                    </div>
                                                                </div>
                                                                {errors.floor && (
                                                                    <p className="text-critical text-xs leading-4 mt-1">
                                                                        {errors.floor}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="roomSection w-full sm:w-auto">
                                                            <div className="flex flex-col gap-1">
                                                                <Label htmlFor="room" className="text-dark-grey dark:text-white font-normal text-sm leading-4 xl:text-base lg:leading-5 tracking-[-0.24px]">Room</Label>
                                                                <div className="flex items-center gap-4">
                                                                    <div className="flex items-center gap-2 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey dark:text-white h-11 bg-white dark:darkThemeGradiantBg  md:w-[272px] sm:w-56 w-full">
                                                                        <input
                                                                            id="room"
                                                                            type="text"
                                                                            name="room"
                                                                            value={unitForm.room}
                                                                            placeholder="Enter Room"
                                                                            className="w-full text-base leading-5 lg:text-lg xl:leading-6  placeholder:text-dark-grey/40 dark:bg-transparent dark:placeholder:text-white/40 dark:text-white font-medium tracking-[-0.408px] outline-none"
                                                                            onChange={handleChange}
                                                                            onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                                                                        />
                                                                        <div className="dropdownIconHolder hidden">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                                <path d="M19.92 8.9502L13.4 15.4702C12.63 16.2402 11.37 16.2402 10.6 15.4702L4.08002 8.9502" stroke="#FF8C65" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {errors.room && (
                                                                    <p className="text-critical text-xs leading-4 mt-1">
                                                                        {errors.room}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="actionsHolder">
                                                    <div className="flex items-center gap-6 justify-center sm:justify-start">
                                                        <button
                                                            type="button"
                                                            data-testid="configure-unit-next-button"
                                                            className={`bg-theme text-white h-10 rounded-md text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[-0.32px] px-5 py-3 flex items-center justify-center grow basis-36 shrink-0 sm:grow-0 hover:bg-theme/80 ${
                                                                isActionsDisabled ? "opacity-50 cursor-not-allowed" : ""
                                                            }`}
                                                            onClick={handleSubmit}
                                                            disabled={isActionsDisabled}
                                                        >Next
                                                        </button>
                                                        <button
                                                            className="bg-white dark:bg-transparent hover:bg-theme dark:hover:bg-theme dark:text-theme hover:text-white dark:hover:text-white transition border border-theme text-theme h-10 rounded-md text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[-0.32px] px-5 py-3 flex items-center justify-center grow basis-36 shrink-0 sm:grow-0"
                                                            onClick={handleGoBack}
                                                        >Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="locationDetails">
                                                <div>
                                                    <h3 className="text-black dark:text-white text-lg leading-5 lg:text-xl lg:leading-6 font-semibold tracking-[0.03px]">Location Details</h3>
                                                    <div className="flex gap-1 mt-3 items-center">
                                                        <div className="iconHolder">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className='*:dark:stroke-white'>
                                                                <path d="M4 10.1433C4 5.64588 7.58172 2 12 2C16.4183 2 20 5.64588 20 10.1433C20 14.6055 17.4467 19.8124 13.4629 21.6744C12.5343 22.1085 11.4657 22.1085 10.5371 21.6744C6.55332 19.8124 4 14.6055 4 10.1433Z" stroke="#292D32" strokeWidth="1.5" />
                                                                <circle cx="12" cy="10" r="3" stroke="#292D32" strokeWidth="1.5" />
                                                            </svg>
                                                        </div>
                                                        <p
                                                            title={[
                                                                locationDetails?.data?.unit?.metadata?.propertyName,
                                                                locationDetails?.data?.unit?.metadata?.buildingName,
                                                                locationDetails?.data?.unit?.metadata?.unit,
                                                                locationDetails?.data?.unit?.metadata?.floor ? `Floor - ${locationDetails.data.unit.metadata.floor}` : null,
                                                                locationDetails?.data?.unit?.metadata?.room ? `Room - ${locationDetails.data.unit.metadata.room}` : null,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" / ")}
                                                            className="text-dark-grey dark:text-white text-base leading-5 lg:text-lg lg:leading-6 font-semibold tracking-[0.027px] truncate w-96 line-clamp-2"
                                                        >
                                                            {[
                                                                locationDetails?.data?.unit?.metadata?.propertyName,
                                                                locationDetails?.data?.unit?.metadata?.buildingName,
                                                                locationDetails?.data?.unit?.metadata?.unit,
                                                                locationDetails?.data?.unit?.metadata?.floor ? `Floor - ${locationDetails.data.unit.metadata.floor}` : null,
                                                                locationDetails?.data?.unit?.metadata?.room ? `Room - ${locationDetails.data.unit.metadata.room}` : null,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" / ")}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="podInfo h-full flex flex-col gap-6">
                                                <div className="flex flex-col">
                                                    <h3 className="text-black dark:text-white text-lg leading-5 lg:text-xl lg:leading-6 font-semibold tracking-[0.03px]" data-testid="configure-unit-zone-heading">
                                                        {awaitingConnection ? PENDING_ZONE_HEADING : "Assign to a Zone"}
                                                    </h3>
                                                    <p className="text-dark-grey dark:text-white text-xs font-normal leading-4" data-testid="configure-unit-zone-helper-text">
                                                        {awaitingConnection
                                                            ? PENDING_ZONE_HELPER_TEXT
                                                            : "Add this unit to a Zone to complete configuration and enable full control."}
                                                    </p>
                                                </div>
                                                <form className="h-full flex flex-col gap-10">
                                                    <div className="flex flex-col gap-6 grow">
                                                        <div className="podsection">
                                                            <div className="flex flex-col gap-1">
                                                                <Label className="text-dark-grey dark:text-white font-normal text-sm leading-4 xl:text-base lg:leading-5 tracking-[-0.24px]">Zone</Label>
                                                                <div className="flex items-center gap-4">
                                                                    <div
                                                                        className={`flex items-center gap-2 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey dark:text-white h-11 bg-white dark:darkThemeGradiantBg dark:border-none md:w-[272px] sm:w-56 w-full relative group ${selectedZone ? "" : "cursor-not-allowed"}`}
                                                                        ref={dropdownFleetRef} onClick={handleFleetDropdown}
                                                                    >
                                                                        <div id="pod" data-testid="configure-unit-zone-trigger" className="w-full flex justify-between items-center cursor-pointer gap-2">
                                                                            <div className="dropdownValue text-base leading-5 lg:text-lg xl:leading-6 font-medium tracking-[-0.408px] grow truncate w-36">
                                                                                {isZoneManuallySelected ? selectedZone?.name : "Select a Zone"}
                                                                            </div>
                                                                            <div className="dropdownIconHolder cursor-pointer">
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                                    <path d="M19.92 8.9502L13.4 15.4702C12.63 16.2402 11.37 16.2402 10.6 15.4702L4.08002 8.9502" stroke="#FF8C65" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"></path>
                                                                                </svg>
                                                                            </div>
                                                                        </div>
                                                                        {showFleetDropdown && (
                                                                            <div className="dropdownMenuHolder dark:bg-darkThemeDropdownBg absolute top-full left-0 right-0 shadow-appShadow dark:shadow-darkThemeDropdownMenuShadow w-full mt-2 rounded-md z-50 max-h-36 overflow-y-auto">
                                                                                <ul className="bg-white dark:bg-transparent rounded-lg w-full">
                                                                                    {allPodData?.zones?.map((fleet, index) => (
                                                                                        <li
                                                                                            key={index}
                                                                                            data-testid={`configure-unit-zone-option-${fleet.unid}`}
                                                                                            onClick={(e) => {
                                                                                                handleSelectFleet(fleet);
                                                                                            }}
                                                                                            className={`px-3 py-2.5 text-dark-grey dark:text-white text-xs text-left leading-4 lg:text-sm lg:leading-5 font-normal tracking-[-0.408px] border-b border-grey dark:border-darkThemeBorder flex justify-between items-center gap-2 cursor-pointer 
                                                                                            ${fleet.unid === selectedZone?.unid ? "bg-[#FFF1C6] dark:bg-darkThemeDropdownItemBg" : ""} ${index === allPodData.length - 1 ? "border-b-0" : ""}`}
                                                                                        >
                                                                                            <span className='basis-10/12 truncate'>{fleet.name}</span>
                                                                                            {fleet.unid === selectedZone?.unid && (
                                                                                                <span>
                                                                                                    <svg
                                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                                        width="20"
                                                                                                        height="20"
                                                                                                        viewBox="0 0 20 20"
                                                                                                        fill="none"
                                                                                                    >
                                                                                                        <path
                                                                                                            d="M8.49215 15C8.24102 15 8.00244 14.8995 7.82665 14.7238L4.27311 11.1702C3.90896 10.8061 3.90896 10.2033 4.27311 9.8392C4.63725 9.47506 5.23997 9.47506 5.60412 9.8392L8.49215 12.7272L14.9463 6.27311C15.3104 5.90896 15.9132 5.90896 16.2773 6.27311C16.6414 6.63725 16.6414 7.23997 16.2773 7.60412L9.15766 14.7238C8.98186 14.8995 8.74329 15 8.49215 15Z"
                                                                                                            fill="#FF8C65"
                                                                                                        />
                                                                                                    </svg>
                                                                                                </span>
                                                                                            )}
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>)}
                                                                    </div>
                                                                    <div className="addIconHolder flex justify-center items-center basis-10 shrink-0">
                                                                        <button
                                                                            type="button"
                                                                            data-testid="configure-unit-add-zone-button"
                                                                            className={`text-theme text-base font-semibold leading-6 ${
                                                                                isActionsDisabled ? "opacity-50 cursor-not-allowed" : ""
                                                                            }`}
                                                                            onClick={(e) => {
                                                                                if (isActionsDisabled) return;
                                                                                handleCreateZoneModal(e);
                                                                            }}
                                                                            disabled={isActionsDisabled}
                                                                        >
                                                                            Add
                                                                        </button>
                                                                    </div>
                                                                    {isZoneManuallySelected && (
                                                                        <div className="removeIconHolder flex justify-center items-center basis-10 shrink-0">
                                                                            <button
                                                                                type="button"
                                                                                data-testid="configure-unit-remove-zone-button"
                                                                                className={`text-theme text-base font-semibold leading-6 ${
                                                                                    isActionsDisabled ? "opacity-50 cursor-not-allowed" : ""
                                                                                }`}
                                                                                onClick={() => {
                                                                                    if (isActionsDisabled) return;
                                                                                    handleRemoveZone();
                                                                                }}
                                                                                disabled={isActionsDisabled}
                                                                            >
                                                                                Remove
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="actionsHolder flex items-center gap-4 sm:gap-6 justify-center flex-col-reverse sm:flex-row sm:justify-between flex-wrap lg:gap-10 w-full">
                                                        <div className="flex items-center gap-6 justify-center sm:justify-start w-full sm:w-auto">
                                                            <button
                                                                data-testid="configure-unit-save-button"
                                                                className={`bg-theme text-white h-10 rounded-md text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[-0.32px] px-5 py-3 flex items-center justify-center basis-36 shrink-0 grow sm:grow-0 hover:bg-theme/80 ${
                                                                    isActionsDisabled ? "opacity-50 cursor-not-allowed" : ""
                                                                }`}
                                                                onClick={(e) => {
                                                                    if (isActionsDisabled) return;
                                                                    handleAddUnitToFleet(e);
                                                                }}
                                                                disabled={isActionsDisabled}
                                                            >
                                                                Save
                                                            </button>
                                                            <button className="bg-white dark:bg-transparent hover:bg-theme dark:hover:bg-theme dark:text-theme hover:text-white dark:hover:text-white transition border border-theme text-theme h-10 rounded-md text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[-0.32px] px-5 py-3 flex items-center justify-center basis-36 shrink-0 grow sm:grow-0" onClick={handleGoBack}>Cancel</button>
                                                        </div>
                                                        <button className="bg-white dark:bg-transparent hover:bg-theme dark:hover:bg-theme dark:text-theme hover:text-white dark:hover:text-white transition border border-theme text-theme h-10 rounded-md text-sm leading-4 lg:text-base lg:leading-5 font-semibold tracking-[-0.32px] px-5 py-3 flex items-center justify-center w-full sm:basis-36 shrink-0 grow-0" type="button" data-testid="configure-unit-back-button" onClick={() => setShowFleetSection(false)}>Back</button>
                                                    </div>
                                                </form>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        {showFleetSection ? (
                            <div className="imageHolder justify-end lg:w-[300px] xl:w-[420px] hidden lg:flex shrink grow absolute right-0 top-0 bottom-0">
                                <img src={ConfigUnitsImg} alt="configUnit" className="object-cover dark:rounded-lg" />
                            </div>
                        ) : (
                            <div className="imageHolder justify-end lg:w-[300px] xl:w-[420px] hidden lg:flex shrink grow absolute right-0 top-0 bottom-0">
                                <img src={ConfigUnits} alt="configUnit" className="object-cover dark:rounded-lg" />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showPropertyModal && (
                <PropertyCard setShowPropertyModal={setShowPropertyModal} onPropertyCreated={onPropertyCreated} />
            )}

            {showBuildingModal && (
                <BuildingCard
                    setShowBuildingModal={setShowBuildingModal}
                    setBuildingOptions={setBuildingOptions}
                    buildingOptions={buildingOptions}
                    allProps={allProps}
                    propertyName={propertyNameValue ? propertyNameValue : "no property selected"}
                    handleBuildingSelect={handleBuildingSelect}
                    fetchAllProperty={fetchAllProperty}
                    isBuildingDisabled={isBuildingDisabled}
                    setIsBuildingDisabled={setIsBuildingDisabled}
                    onBuildingCreated={handleBuildingCreated}
                    setAllProps={setAllProps}
                    onActionSuccess={(message, type) => showToast(message, type)}
                />
            )}
            {showConfirmModal && (
                <ConfirmModal
                    title="Cancel Configure Unit?"
                    confirmMessage="You have unsaved changes.Do you want to cancel and discard the configuration?"
                    cancelText="Cancel"
                    submitText="Confirm"
                    onCancel={handleCancelDelete}
                    onSubmit={handleConfirmBack}
                />
            )}
            {showUnitChangeModel && (
                <ConfirmModal
                    title="Change Unit Name?"
                    confirmMessage="This action will change the previous unit name."
                    cancelText="Cancel"
                    submitText="Confirm"
                    onCancel={handleCancelChangeUnit}
                    onSubmit={handleChangeUnit}
                />
            )}
            {createZoneModal && (
                <PodModal
                    showPodModal={createZoneModal}
                    setShowPodModal={setShowCreateZoneModal}
                    onCreateSuccess={(newZone) => {
                        showToast("Created Successfully", "success");
                        const currentPodData = useFleetStore.getState().allPodData;
                        const podData = currentPodData && !Array.isArray(currentPodData) ? currentPodData : {};
                        const fleets = Array.isArray(podData?.zones) ? podData.zones : [];
                        const hasNewZone = fleets.some((fleet) => fleet?.unid === newZone?.unid || fleet?.name === newZone?.name);

                        if (!hasNewZone) {
                            setAllPodData({
                                ...podData,
                                zones: [...fleets, newZone]
                            });
                        }

                        setSelectedFleet(newZone);
                        setSelectedZone(newZone);
                        setShowCreateZoneModal(false);
                        setIsZoneManuallySelected(true);
                    }}
                />
            )}

            <ToastMessage
                message={toast.message}
                type={toast.type}
                show={toast.show}
                onClose={() => setToast(prev => ({ ...prev, show: false }))}
            />

        </>
    );
};

export default ConfigureUnits;
